import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/Button";
import Alert from "../components/ui/Alert";
import { Input } from "../components/ui/Input";
import { apiService } from "../utils/apiService";
import { useLoadingState } from "../hooks/useLoadingState";
import { useAutoAlert } from "../hooks/useAutoAlert";
import { FiMail, FiEye, FiRefreshCw, FiUsers, FiCalendar, FiAward } from "react-icons/fi";

const AdminEmailTemplates = () => {
  const { user } = useAuth();
  const { loading, withLoading } = useLoadingState();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Template selection
  const [selectedTemplate, setSelectedTemplate] = useState("password-reset");
  
  // Form data for different templates
  const [formData, setFormData] = useState({
    email: "",
    userName: "",
    gameIds: [],
    leagueIds: [],
    week: 1,
  });
  
  // Available data for dropdowns
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableGames, setAvailableGames] = useState([]);
  const [availableLeagues, setAvailableLeagues] = useState([]);
  
  // Preview data
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Auto-dismiss alerts after 5 seconds
  useAutoAlert(error, setError);
  useAutoAlert(success, setSuccess);

  useEffect(() => {
    if (user?.isAdmin) {
      fetchAvailableData();
    }
  }, [user]);

  const fetchAvailableData = async () => {
    try {
      const [usersRes, gamesRes, leaguesRes] = await Promise.all([
        apiService.get("/admin/email-templates/available/users"),
        apiService.get("/admin/email-templates/available/games"),
        apiService.get("/admin/email-templates/available/leagues"),
      ]);
      
      setAvailableUsers(usersRes.users || []);
      setAvailableGames(gamesRes.games || []);
      setAvailableLeagues(leaguesRes.leagues || []);
    } catch (err) {
      // Silently handle error for available data
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGameSelection = (gameId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        gameIds: [...prev.gameIds, gameId],
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        gameIds: prev.gameIds.filter(id => id !== gameId),
      }));
    }
  };

  const handleLeagueSelection = (leagueId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        leagueIds: [...prev.leagueIds, leagueId],
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        leagueIds: prev.leagueIds.filter(id => id !== leagueId),
      }));
    }
  };

  const generatePreview = async () => {
    setError("");
    setSuccess("");
    
    try {
      let response;
      
      switch (selectedTemplate) {
        case "password-reset":
          response = await withLoading(async () => {
            return await apiService.post("/admin/email-templates/preview/password-reset", {
              email: formData.email,
              userName: formData.userName,
            });
          });
          break;
          
        case "pick-reminder":
          if (!formData.gameIds.length) {
            setError("Please select at least one game for pick reminder preview");
            return;
          }
          response = await withLoading(async () => {
            return await apiService.post("/admin/email-templates/preview/pick-reminder", {
              email: formData.email,
              userName: formData.userName,
              gameIds: formData.gameIds,
            });
          });
          break;
          
        case "weekly-summary":
          if (!formData.leagueIds.length) {
            setError("Please select at least one league for weekly summary preview");
            return;
          }
          response = await withLoading(async () => {
            return await apiService.post("/admin/email-templates/preview/weekly-summary", {
              email: formData.email,
              userName: formData.userName,
              leagueIds: formData.leagueIds,
              week: formData.week,
            });
          });
          break;
          
        default:
          setError("Invalid template selected");
          return;
      }
      
      setPreviewData(response);
      setShowPreview(true);
      setSuccess("Preview generated successfully!");
    } catch (err) {
      apiService.handleError(err, setError, "Failed to generate preview");
    }
  };

  const renderTemplateForm = () => {
    switch (selectedTemplate) {
      case "password-reset":
        return (
          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              placeholder="user@example.com"
              required
            />
            <Input
              label="User Name (Optional)"
              value={formData.userName}
              onChange={(e) => handleFormChange("userName", e.target.value)}
              placeholder="John Doe"
            />
          </div>
        );
        
      case "pick-reminder":
        return (
          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              placeholder="user@example.com"
              required
            />
            <Input
              label="User Name"
              value={formData.userName}
              onChange={(e) => handleFormChange("userName", e.target.value)}
              placeholder="John Doe"
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Games (Max 5)
              </label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                {availableGames.map((game) => (
                  <label key={game.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.gameIds.includes(game.id)}
                      onChange={(e) => handleGameSelection(game.id, e.target.checked)}
                      disabled={formData.gameIds.length >= 5 && !formData.gameIds.includes(game.id)}
                    />
                    <span className="text-sm">
                      {game.away_team_name} @ {game.home_team_name} - {new Date(game.game_date).toLocaleDateString()}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );
        
      case "weekly-summary":
        return (
          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              placeholder="user@example.com"
              required
            />
            <Input
              label="User Name"
              value={formData.userName}
              onChange={(e) => handleFormChange("userName", e.target.value)}
              placeholder="John Doe"
              required
            />
            <Input
              label="Week Number"
              type="number"
              min="1"
              max="18"
              value={formData.week}
              onChange={(e) => handleFormChange("week", parseInt(e.target.value))}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Leagues (Max 3)
              </label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                {availableLeagues.map((league) => (
                  <label key={league.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.leagueIds.includes(league.id)}
                      onChange={(e) => handleLeagueSelection(league.id, e.target.checked)}
                      disabled={formData.leagueIds.length >= 3 && !formData.leagueIds.includes(league.id)}
                    />
                    <span className="text-sm">{league.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const renderPreview = () => {
    if (!previewData || !showPreview) return null;
    
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Email Preview</h3>
          <Button
            variant="secondary"
            onClick={() => setShowPreview(false)}
            size="sm"
          >
            Hide Preview
          </Button>
        </div>
        
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-md">
            <strong>Subject:</strong> {previewData.subject}
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              HTML Content:
            </label>
            <div className="border rounded-md p-3 bg-gray-50 max-h-96 overflow-y-auto">
              <div dangerouslySetInnerHTML={{ __html: previewData.html }} />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Plain Text Content:
            </label>
            <div className="border rounded-md p-3 bg-gray-50 max-h-48 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm">{previewData.text}</pre>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!user?.isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600 mt-2">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-3">
          <FiMail className="text-blue-600" />
          Email Template Testing Tool
        </h1>
        <p className="text-gray-600 mt-2">
          Test and preview email templates with mock data
        </p>
      </div>

      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template Selection and Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Template Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiEye className="text-blue-600" />
              Template Selection
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <button
                onClick={() => setSelectedTemplate("password-reset")}
                className={`p-4 border rounded-lg text-center transition-colors ${
                  selectedTemplate === "password-reset"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <FiMail className="mx-auto mb-2 text-xl" />
                <div className="font-medium">Password Reset</div>
                <div className="text-sm text-gray-500">Reset password emails</div>
              </button>
              
              <button
                onClick={() => setSelectedTemplate("pick-reminder")}
                className={`p-4 border rounded-lg text-center transition-colors ${
                  selectedTemplate === "pick-reminder"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <FiCalendar className="mx-auto mb-2 text-xl" />
                <div className="font-medium">Pick Reminder</div>
                <div className="text-sm text-gray-500">Daily pick reminders</div>
              </button>
              
              <button
                onClick={() => setSelectedTemplate("weekly-summary")}
                className={`p-4 border rounded-lg text-center transition-colors ${
                  selectedTemplate === "weekly-summary"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <FiAward className="mx-auto mb-2 text-xl" />
                <div className="font-medium">Weekly Summary</div>
                <div className="text-sm text-gray-500">Weekly performance summaries</div>
              </button>
            </div>
            
            {/* Template Form */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Template Configuration
              </h3>
              {renderTemplateForm()}
              
              <div className="mt-6">
                <Button
                  onClick={generatePreview}
                  loading={loading}
                  className="w-full md:w-auto"
                >
                  <FiRefreshCw className="mr-2" />
                  Generate Preview
                </Button>
              </div>
            </div>
          </div>
          
          {/* Preview */}
          {renderPreview()}
        </div>
        
        {/* Available Data Panel */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiUsers className="text-green-600" />
              Available Data
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Users</h3>
                <div className="text-sm text-gray-600">
                  {availableUsers.length} users available
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Games</h3>
                <div className="text-sm text-gray-600">
                  {availableGames.length} games available
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Leagues</h3>
                <div className="text-sm text-gray-600">
                  {availableLeagues.length} leagues available
                </div>
              </div>
              
              <Button
                onClick={fetchAvailableData}
                variant="secondary"
                size="sm"
                className="w-full"
              >
                <FiRefreshCw className="mr-2" />
                Refresh Data
              </Button>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">How to Use</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Select a template type</li>
              <li>• Fill in the required fields</li>
              <li>• Click &quot;Generate Preview&quot;</li>
              <li>• Review the HTML and text versions</li>
              <li>• All links are mocked (no real actions)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates; 